import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@arcadeai/design-system/components/ui/atoms/select";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@arcadeai/design-system/components/ui/atoms/table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  Plus,
  Settings,
  Settings2,
  X,
} from "lucide-react";
import { ListLayout } from "@/components/custom/list-layout/list-layout";

export const ProjectsListSkeleton = () => {
  return (
    <ListLayout
      actions={
        <Button disabled size="sm">
          <Plus className="mr-0 size-4 sm:mr-2" />
          <span className="hidden sm:block">Add Project</span>
        </Button>
      }
      description="Organize resources, manage team access, and separate environments for your workflows."
      title="Projects"
    >
      <div className="my-2 space-y-4">
        {/* Toolbar skeleton matching DataTableToolbar structure */}
        <div className="flex flex-col items-start justify-between gap-2 lg:flex-row lg:items-center">
          <div className="flex w-full flex-col items-start gap-2 lg:flex-1 lg:flex-row lg:items-center">
            <Input
              className="h-8 w-full max-w-[250px] text-sm lg:w-[200px] xl:w-[250px]"
              disabled
              placeholder="Search by name"
            />
            <div className="flex w-full flex-wrap gap-2 lg:w-auto">
              <Button className="h-8 px-2 lg:px-3" disabled variant="warning">
                Reset
                <X className="ml-1 size-4" />
              </Button>
            </div>
          </div>
          <Button
            className="ml-auto hidden h-8 lg:flex"
            disabled
            size="sm"
            variant="outline"
          >
            <Settings2 />
            Columns
          </Button>
        </div>

        {/* Table skeleton matching projects columns structure */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <div className="flex items-center space-x-2">
                    <span>Name</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center space-x-2">
                    <span>Description</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center space-x-2">
                    <span>Created</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }, () => (
                <ProjectRowSkeleton key={crypto.randomUUID()} />
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination skeleton matching DataTablePagination */}
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center space-x-2">
            <p className="font-medium text-sm">Rows per page</p>
            <Select disabled>
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder="10" />
              </SelectTrigger>
              <SelectContent side="top">
                <SelectItem value="10">10</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-6 lg:space-x-8">
            <div className="flex w-[100px] items-center justify-center font-medium text-sm">
              Page 1 of <Skeleton className="ml-1 inline-block h-4 w-6" />
            </div>
            <div className="flex items-center space-x-2">
              <Button
                className="hidden h-8 w-8 p-0 lg:flex"
                disabled
                variant="outline"
              >
                <span className="sr-only">Go to first page</span>
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button className="h-8 w-8 p-0" disabled variant="outline">
                <span className="sr-only">Go to previous page</span>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button className="h-8 w-8 p-0" disabled variant="outline">
                <span className="sr-only">Go to next page</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                className="hidden h-8 w-8 p-0 lg:flex"
                disabled
                variant="outline"
              >
                <span className="sr-only">Go to last page</span>
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ListLayout>
  );
};

const ProjectRowSkeleton = () => {
  return (
    <TableRow>
      {/* Name */}
      <TableCell>
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-4 w-32" />
        </div>
      </TableCell>

      {/* Description */}
      <TableCell className="w-full">
        <Skeleton className="h-4 w-full max-w-lg" />
      </TableCell>

      {/* Created */}
      <TableCell className="whitespace-nowrap">
        <Skeleton className="h-4 w-24" />
      </TableCell>

      {/* Actions */}
      <TableCell className="whitespace-nowrap">
        <div className="flex items-center gap-1">
          <div className="flex items-center gap-1">
            <Button className="gap-1.5" disabled size="sm" variant="outline">
              <Settings className="size-4" />
              <span className="text-xs">Edit</span>
            </Button>
            <Button className="gap-1.5" disabled size="sm" variant="outline">
              <X className="size-4" />
              <span className="text-xs">Delete</span>
            </Button>
          </div>
          <div className="mx-1 h-4 w-px bg-border" />
          <Button className="gap-1.5" disabled size="sm" variant="ghost">
            <span className="text-xs">Set Active</span>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};
